import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Chip } from './ui/chip';
import { EmptyState } from './ui/empty-state';
import { LoadingState } from './ui/loading-state';
import { useTranslation } from '../i18n/LanguageContext';
import {
  Users, Plus, UserPlus, Mail, Check, X, Crown,
  LogOut, Trash2, Home, Package, ShoppingCart, Edit3, Eye, EyeOff
} from 'lucide-react';

function HouseholdPage() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [householdData, setHouseholdData] = useState(null);
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [householdName, setHouseholdName] = useState('');
  const [inviteIdentifier, setInviteIdentifier] = useState('');
  const [sharedStock, setSharedStock] = useState([]);
  const [showSharedStock, setShowSharedStock] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingQuantity, setEditingQuantity] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [busyRecommendationId, setBusyRecommendationId] = useState(null);

  useEffect(() => {
    loadHousehold();
    loadInvitations();
  }, []);

  const loadHousehold = async () => {
    try {
      const res = await axios.get('/household');
      setHouseholdData(res.data);
      if (res.data.household) {
        loadSharedStock(res.data.household.id);
      }
      loadRecommendations();
    } catch (error) {
      console.error('Error loading household:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInvitations = async () => {
    try {
      const res = await axios.get('/household/invitations/received');
      setReceivedInvitations(res.data);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  const loadSharedStock = async (householdId) => {
    try {
      const res = await axios.get(`/household/${householdId}/stock`);
      setSharedStock(res.data);
    } catch (error) {
      console.error('Error loading shared stock:', error);
    }
  };

  const refreshSharedStock = async () => {
    await loadHousehold();
  };

  const loadRecommendations = async () => {
    try {
      setLoadingRecommendations(true);
      const res = await axios.get('/household/recommendations/restock?limit=10');
      setRecommendations(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (error) {
      console.error('Error loading recommendations:', error);
      setRecommendations([]);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const handleCreate = async () => {
    if (!householdName.trim()) {
      toast.error('Entrez un nom pour le foyer');
      return;
    }
    try {
      const res = await axios.post('/household', { name: householdName.trim() });
      toast.success(res.data.message);
      setHouseholdName('');
      setShowCreate(false);
      loadHousehold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleInvite = async () => {
    if (!inviteIdentifier.trim()) {
      toast.error('Entrez un email ou un nom d\'utilisateur');
      return;
    }
    try {
      const res = await axios.post(
        `/household/${householdData.household.id}/invite`,
        { identifier: inviteIdentifier.trim() }
      );
      toast.success(res.data.message);
      setInviteIdentifier('');
      loadHousehold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleInvitationResponse = async (invitationId, action) => {
    try {
      const res = await axios.post(
        `/household/invitations/${invitationId}/respond`,
        { action }
      );
      toast.success(res.data.message);
      loadInvitations();
      loadHousehold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      const res = await axios.delete(`/household/invitations/${invitationId}`);
      toast.success(res.data.message);
      loadHousehold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Retirer ce membre du foyer ?')) return;
    try {
      const res = await axios.delete(
        `/household/${householdData.household.id}/members/${userId}`
      );
      toast.success(res.data.message);
      loadHousehold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Voulez-vous vraiment quitter ce foyer ?')) return;
    try {
      const res = await axios.post(`/household/${householdData.household.id}/leave`);
      toast.success(res.data.message);
      loadHousehold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer le foyer ? Tous les membres seront retires.')) return;
    try {
      const res = await axios.delete(`/household/${householdData.household.id}`);
      toast.success(res.data.message);
      loadHousehold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const startEditingSharedItem = (stock) => {
    setEditingItemId(stock.item_id);
    setEditingQuantity(String(stock.stock_actuel ?? ''));
  };

  const cancelEditingSharedItem = () => {
    setEditingItemId(null);
    setEditingQuantity('');
  };

  const saveSharedItemQuantity = async (stock) => {
    const nextQuantity = parseFloat(editingQuantity);
    if (!Number.isFinite(nextQuantity) || nextQuantity < 0) {
      toast.error('Entrez une quantite valide');
      return;
    }

    try {
      await axios.put(`/items/${stock.item_id}`, { stock: nextQuantity });
      toast.success('Quantite mise a jour');
      cancelEditingSharedItem();
      await refreshSharedStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise a jour');
    }
  };

  const deleteSharedItem = async (stock) => {
    if (!window.confirm(`Supprimer l'article "${stock.item_nom}" ?`)) return;

    try {
      await axios.delete(`/items/${stock.item_id}`);
      toast.success(`"${stock.item_nom}" supprime avec succes`);
      if (editingItemId === stock.item_id) {
        cancelEditingSharedItem();
      }
      await refreshSharedStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const toggleSupervision = async (recommendation) => {
    try {
      setBusyRecommendationId(recommendation.item_id);
      const watched = !recommendation.is_supervised;
      await axios.post(`/household/supervision/${recommendation.item_id}`, { watched });
      await loadRecommendations();
      toast.success(watched ? 'Article ajoute a la surveillance' : 'Article retire de la surveillance');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de mettre a jour la surveillance');
    } finally {
      setBusyRecommendationId(null);
    }
  };

  const sendRecommendationFeedback = async (recommendation, action) => {
    try {
      setBusyRecommendationId(recommendation.item_id);
      await axios.post('/household/recommendations/feedback', {
        item_id: recommendation.item_id,
        action,
      });
      await loadRecommendations();
      if (action === 'accept') {
        toast.success('Priorite confirmee');
      } else {
        toast.success('Suggestion ignoree');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Feedback impossible');
    } finally {
      setBusyRecommendationId(null);
    }
  };

  if (loading) {
    return <LoadingState label="Chargement du foyer..." />;
  }

  const household = householdData?.household;
  const members = householdData?.members || [];
  const pendingInvitations = householdData?.invitations || [];
  const isOwner = householdData?.my_role === 'OWNER';

  return (
    <div className="space-y-4 min-w-0 overflow-x-clip" data-testid="household-page">
      {/* Received invitations */}
      {receivedInvitations.length > 0 && (
        <Card className="border-blue-200 bg-blue-50" data-testid="received-invitations-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-blue-800">
              <Mail className="w-5 h-5" />
              {t('household.invitations')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {receivedInvitations.map((inv) => (
              <div key={inv.id} className="flex flex-col gap-3 rounded-lg border bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900">{inv.household_name}</p>
                  <p className="text-xs text-gray-500">
                    {t('household.invitedBy')} {inv.invited_by_username}
                  </p>
                  <p className="break-words text-xs text-gray-500">
                    {inv.invited_username || inv.invited_email || 'Invité'}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Button
                    size="sm"
                    onClick={() => handleInvitationResponse(inv.id, 'accept')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3"
                    data-testid={`accept-invitation-${inv.id}`}
                  >
                    <Check size={14} className="mr-1" />
                    {t('household.accept')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleInvitationResponse(inv.id, 'decline')}
                    className="h-8 px-3 text-red-600 border-red-200"
                    data-testid={`decline-invitation-${inv.id}`}
                  >
                    <X size={14} className="mr-1" />
                    {t('household.decline')}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No household - create option */}
      {!household && (
        <Card data-testid="no-household-card">
          <CardContent className="p-6 text-center">
            <div className="p-4 bg-emerald-50 rounded-full inline-block mb-4">
              <Home className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('household.noHousehold')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('household.createInfo')}</p>

            {showCreate ? (
              <div className="mx-auto max-w-xs space-y-3">
                <Input
                  placeholder={t('household.name')}
                  value={householdName}
                  onChange={(e) => setHouseholdName(e.target.value)}
                  className="text-center"
                  data-testid="household-name-input"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1">
                    Annuler
                  </Button>
                  <Button onClick={handleCreate} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-household-button">
                    {t('household.create')}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                onClick={() => setShowCreate(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="show-create-household"
              >
                <Plus size={16} className="mr-2" />
                {t('household.create')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Household exists */}
      {household && (
        <>
          {/* Household info */}
          <Card data-testid="household-info-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Home className="w-5 h-5 text-emerald-600" />
                  {household.name}
                </CardTitle>
                <Badge variant="secondary" className="text-xs">
                  {members.length} {t('household.members').toLowerCase()}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Members list */}
              {members.map((m) => (
                <div key={m.user_id} className="flex flex-col gap-3 border-b border-gray-100 py-2 last:border-0 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      m.role === 'OWNER' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {m.username.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="break-words text-sm font-medium text-gray-900">
                        {m.username}
                        {m.user_id === user?.id && <span className="text-xs text-gray-400 ml-1">(vous)</span>}
                      </p>
                      <p className="break-words text-xs text-gray-500">{m.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    {m.role === 'OWNER' ? (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">
                        <Crown size={10} className="mr-1" />
                        {t('household.owner')}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">{t('household.member')}</Badge>
                    )}
                    {isOwner && m.role !== 'OWNER' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveMember(m.user_id)}
                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-member-${m.user_id}`}
                      >
                        <X size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {/* Pending invitations */}
              {pendingInvitations.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs font-medium text-gray-500 mb-2">{t('household.pending')}</p>
                  {pendingInvitations.map((inv) => (
                    <div key={inv.id} className="flex flex-col gap-2 py-1.5 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="min-w-0 break-words text-sm text-gray-600">{inv.invited_username}</span>
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">
                          {t('household.pending')}
                        </Badge>
                      </div>
                      {isOwner && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCancelInvitation(inv.id)}
                          className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                          data-testid={`cancel-invitation-${inv.id}`}
                        >
                          <X size={12} className="mr-1" />
                          Annuler
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Invite form */}
              {isOwner && (
                <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center">
                  <Input
                    placeholder={t('household.invitePlaceholder')}
                    value={inviteIdentifier}
                    onChange={(e) => setInviteIdentifier(e.target.value)}
                    className="h-9 flex-1 text-sm"
                    data-testid="invite-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                  />
                  <Button
                    size="sm"
                    onClick={handleInvite}
                    className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-3"
                    data-testid="invite-button"
                  >
                    <UserPlus size={14} className="mr-1" />
                    {t('household.invite')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Shared stock toggle */}
          <Button
            variant="outline"
            onClick={() => setShowSharedStock(!showSharedStock)}
            className="w-full"
            data-testid="toggle-shared-stock"
          >
            <Package size={16} className="mr-2" />
            {t('household.sharedStock')} ({sharedStock.length})
          </Button>

          {/* Shared stock view */}
          {showSharedStock && sharedStock.length > 0 && (
            <div className="space-y-2" data-testid="shared-stock-list">
              {sharedStock.map((stock, i) => {
                const canManageSharedItem = isOwner || stock.user_id === user?.id;
                return (
                <Card key={i} className={`${stock.en_alerte ? 'border-red-200 bg-red-50' : ''}`}>
                  <CardContent className="p-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="break-words text-sm font-medium text-gray-900">{stock.item_nom}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {editingItemId === stock.item_id ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.1"
                                value={editingQuantity}
                                onChange={(e) => setEditingQuantity(e.target.value)}
                                className="h-8 w-24 text-xs"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    saveSharedItemQuantity(stock);
                                  }
                                  if (e.key === 'Escape') {
                                    cancelEditingSharedItem();
                                  }
                                }}
                              />
                              <span className="text-xs text-gray-500">{stock.unite}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">
                              {stock.stock_actuel} {stock.unite}
                            </span>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {stock.owner_username}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                        {stock.en_alerte && (
                          <Badge variant="destructive" className="text-xs">Alerte</Badge>
                        )}
                        {canManageSharedItem && (
                          editingItemId === stock.item_id ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => saveSharedItemQuantity(stock)}
                                className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`save-shared-item-${stock.item_id}`}
                              >
                                <Check size={12} className="mr-1" />
                                Sauver
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEditingSharedItem}
                                className="h-8 px-2"
                              >
                                <X size={12} className="mr-1" />
                                Annuler
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => startEditingSharedItem(stock)}
                                className="h-8 px-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                                data-testid={`edit-shared-item-${stock.item_id}`}
                              >
                                <Edit3 size={12} className="mr-1" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteSharedItem(stock)}
                                className="h-8 px-2 text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`delete-shared-item-${stock.item_id}`}
                              >
                                <Trash2 size={12} className="mr-1" />
                                Supprimer
                              </Button>
                            </>
                          )
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
          {showSharedStock && sharedStock.length === 0 && (
            <EmptyState
              title="Stock partage vide"
              description="Aucun article partage dans ce foyer pour le moment."
            />
          )}

          <Card data-testid="household-recommendations-card">
            <CardHeader>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-emerald-600" />
                  Restock intelligent
                </CardTitle>
                <Chip tone="primary">{recommendations.length}/10 priorites</Chip>
              </div>
              <CardDescription>
                Suggestions utiles selon la frequence d&apos;achat, le risque de rupture et la surveillance active.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {loadingRecommendations ? (
                <LoadingState label="Analyse des habitudes d'achat..." className="p-2" />
              ) : recommendations.length === 0 ? (
                <EmptyState
                  title="Pas de priorite urgente"
                  description="Le foyer est stable. Les prochaines recommandations apparaitront automatiquement."
                />
              ) : (
                recommendations.map((recommendation) => {
                  const isBusy = busyRecommendationId === recommendation.item_id;
                  return (
                    <div key={recommendation.item_id} className="rounded-xl border border-emerald-100 bg-white p-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900 break-words">{recommendation.nom}</p>
                          <p className="text-xs text-gray-500">{recommendation.categorie}</p>
                          <p className="mt-1 text-xs text-gray-700">{recommendation.explanation}</p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {(recommendation.reason_codes || []).map((code) => (
                              <Chip key={`${recommendation.item_id}-${code}`} tone="warning">{code}</Chip>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-row flex-wrap gap-2 sm:flex-col sm:flex-nowrap">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isBusy}
                            onClick={() => toggleSupervision(recommendation)}
                            data-testid={`toggle-supervision-${recommendation.item_id}`}
                          >
                            {recommendation.is_supervised ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}
                            {recommendation.is_supervised ? 'Retirer' : 'Surveiller'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            disabled={isBusy}
                            onClick={() => sendRecommendationFeedback(recommendation, 'accept')}
                            data-testid={`accept-recommendation-${recommendation.item_id}`}
                          >
                            <Check size={14} className="mr-1" />
                            Prioriser
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isBusy}
                            onClick={() => sendRecommendationFeedback(recommendation, 'ignore')}
                            data-testid={`ignore-recommendation-${recommendation.item_id}`}
                          >
                            <X size={14} className="mr-1" />
                            Ignorer
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col gap-2 sm:flex-row">
            {!isOwner && (
              <Button
                variant="outline"
                onClick={handleLeave}
                className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                data-testid="leave-household-button"
              >
                <LogOut size={14} className="mr-2" />
                {t('household.leave')}
              </Button>
            )}
            {isOwner && (
              <Button
                variant="outline"
                onClick={handleDelete}
                className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                data-testid="delete-household-button"
              >
                <Trash2 size={14} className="mr-2" />
                {t('household.delete')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default HouseholdPage;
